import { httpClient, ApiResponse } from '@/utils';
import {
  CategoriesEndpoints,
  ICategoriesResponse,
  ICategoryDetailsResponse,
  ICreateCategoryResponse,
  IDeleteCategoryResponse,
  IEditCategoryDTO,
  ISubCategoriesResponse,
  IEditCategoryResponse,
  ICreateCategoryDTO,
  IChangeCategoryPosition,
  ICreatePopularCategoriesDTO,
  IUpdateFiltersCategoryDTO,
  ICategoriesAutocompleteDTO,
  IGetCategoryStatsDTO,
  IGetCategoryStatsResponse,
  ICategoryFilterValuesResponse,
  ICategoryFilterValuesQuery,
  IUpdateCategoryFilterValuesDTO,
  IUpdateCategorySeoFiltersDTO,
  IMarketplaceCategoriesResponse,
  IUpdateMarketplaceMappingsDTO,
  IUpdateMarketplaceMappingsResponse,
} from '@/models/categories.model';

class CategoriesAPI {
  static getCategories(limit = 500): ApiResponse<ICategoriesResponse> {
    return httpClient.get<ICategoriesResponse>(CategoriesEndpoints.CATEGORIES, { params: { limit } });
  }
  static getCategoriesAutocomplete(data: ICategoriesAutocompleteDTO): ApiResponse<ICategoriesResponse> {
    const { name, limit, page } = data;
    return httpClient.get<ICategoriesResponse>(CategoriesEndpoints.CATEGORIES_AUTOCOMPLETE, {
      params: { name, limit, has_children: data.has_children, page },
    });
  }

  static getPopularCategories(): ApiResponse<ICategoriesResponse> {
    return httpClient.get<ICategoriesResponse>(CategoriesEndpoints.POPULAR_CATEGORIES);
  }

  static createPopularCategories(data: ICreatePopularCategoriesDTO): ApiResponse<ICategoriesResponse> {
    return httpClient.post<ICategoriesResponse>(CategoriesEndpoints.POPULAR_CATEGORIES, data);
  }

  static updateFiltersCategory(data: IUpdateFiltersCategoryDTO, id?: string): ApiResponse<ICategoryDetailsResponse> {
    return httpClient.post<ICategoryDetailsResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}/filters`, data);
  }

  static getSubCategories(id: string): ApiResponse<ISubCategoriesResponse> {
    return httpClient.get<ISubCategoriesResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}`);
  }

  static getCategoryDetails(id: string): ApiResponse<ICategoryDetailsResponse> {
    return httpClient.get<ICategoryDetailsResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}`);
  }

  static deleteCategory(id: string): ApiResponse<IDeleteCategoryResponse> {
    return httpClient.delete<IDeleteCategoryResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}`);
  }

  static toggleActiveCategory(id: string): ApiResponse<IDeleteCategoryResponse> {
    return httpClient.patch<IDeleteCategoryResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}/toggle-active`);
  }

  static editCategory(id: string, data: IEditCategoryDTO): ApiResponse<IEditCategoryResponse> {
    const formData = new FormData();

    data.locale?.forEach((locale) => {
      formData.append(`locale[${locale.locale}][name]`, locale.name ?? '');
      if (locale.seo_title || locale.seo_title === '') {
        formData.append(`locale[${locale.locale}][seo_title]`, locale.seo_title ?? '');
      }
      if (locale.seo_description || locale.seo_description === '') {
        formData.append(`locale[${locale.locale}][seo_description]`, locale.seo_description ?? '');
      }
      if (locale.seo_text || locale.seo_text === '') {
        formData.append(`locale[${locale.locale}][seo_text]`, locale.seo_text ?? '');
      }
      if (!locale.keywords?.length) {
        formData.append(`locale[${locale.locale}][keywords]`, '');
      } else {
        locale.keywords.forEach((element) => formData.append(`locale[${locale.locale}][keywords][]`, element));
      }
    });

    if (data.google_feed_id) formData.append('google_feed_id', data.google_feed_id.toString());
    if (data.slug) formData.append('slug', data.slug);
    if (data.parent_id) formData.append('parent_id', data.parent_id);
    if (data.photo) formData.append('photo', data.photo);
    formData.append('is_restoration', data.is_restoration ? '1' : '0');
    formData.append('is_heavy', data.is_heavy ? '1' : '0');

    return httpClient.post<IEditCategoryResponse>(`${CategoriesEndpoints.CATEGORIES}/${id}`, formData);
  }

  static createCategory(data: ICreateCategoryDTO): ApiResponse<ICreateCategoryResponse> {
    const formData = new FormData();

    data.locale?.forEach((locale) => {
      formData.append(`locale[${locale.locale}][name]`, locale.name ?? '');
    });

    if (data.google_feed_id) formData.append('google_feed_id', data.google_feed_id.toString());
    if (data.parent_id) formData.append('parent_id', data.parent_id);
    if (data.photo) formData.append('photo', data.photo);
    formData.append('is_heavy', data.is_heavy ? '1' : '0');

    return httpClient.post<ICreateCategoryResponse>(CategoriesEndpoints.CATEGORIES, formData);
  }

  static changeCategoryPosition(id: string, position: number): ApiResponse<IChangeCategoryPosition> {
    return httpClient.patch<IChangeCategoryPosition>(`${CategoriesEndpoints.CATEGORIES}/${id}/change-position`, {
      position,
    });
  }

  static getCategoryStats(data: IGetCategoryStatsDTO): ApiResponse<IGetCategoryStatsResponse> {
    return httpClient.get<IGetCategoryStatsResponse>(CategoriesEndpoints.CATEGORY_STATS, { params: data });
  }

  static updateCategoryFilterMinMax(
    categoryId: string,
    filterId: string,
    data: { min?: number | null; max?: number | null },
  ): ApiResponse<ICategoryDetailsResponse> {
    return httpClient.patch<ICategoryDetailsResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/filters/${filterId}/min-max`,
      data,
    );
  }

  static getCategoryFilterValues(
    categoryId: string,
    filterId: string,
    params?: ICategoryFilterValuesQuery,
  ): ApiResponse<ICategoryFilterValuesResponse> {
    return httpClient.get<ICategoryFilterValuesResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/filters/${filterId}/values`,
      { params },
    );
  }

  static updateCategoryFilterValues(
    categoryId: string,
    filterId: string,
    data: IUpdateCategoryFilterValuesDTO,
  ): ApiResponse<ICategoryFilterValuesResponse> {
    return httpClient.patch<ICategoryFilterValuesResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/filters/${filterId}/values`,
      data,
    );
  }

  static detachCategoryFilter(categoryId: string, filterId: string): ApiResponse<ICategoryDetailsResponse> {
    return httpClient.delete<ICategoryDetailsResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/filters/${filterId}`,
    );
  }

  static updateCategorySeoFilters(
    categoryId: string,
    data: IUpdateCategorySeoFiltersDTO,
  ): ApiResponse<ICategoryDetailsResponse> {
    return httpClient.patch<ICategoryDetailsResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/seo-filters`,
      data,
    );
  }

  static searchMarketplaceCategories(params: {
    marketplace: string;
    q?: string;
    unmapped?: boolean;
    limit?: number;
  }): ApiResponse<IMarketplaceCategoriesResponse> {
    return httpClient.get<IMarketplaceCategoriesResponse>(CategoriesEndpoints.MARKETPLACE_CATEGORIES, { params });
  }

  static updateMarketplaceMappings(
    categoryId: string,
    data: IUpdateMarketplaceMappingsDTO,
  ): ApiResponse<IUpdateMarketplaceMappingsResponse> {
    return httpClient.put<IUpdateMarketplaceMappingsResponse>(
      `${CategoriesEndpoints.CATEGORIES}/${categoryId}/marketplace-mappings`,
      data,
    );
  }
}

export default CategoriesAPI;
